import React, { useState, useEffect, useCallback } from "react";
import { FaPlus, FaTrash, FaEdit, FaReply } from "react-icons/fa";
import ReactQuill from 'react-quill-new'; 
import 'react-quill-new/dist/quill.snow.css';
import "../styles/BlogManager.css"; 

const API = "https://backend.afrivisionconsultants.com";
// const API_URL = "http://localhost:5000"; 

export default function BlogManager({ token }) {
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  
  // Post Form State
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ 
    title: "", excerpt: "", content: "", categoryId: "", scheduledAt: "", keywords: "", allowComments: true 
  });
  const [imageFile, setImageFile] = useState(null);

  // Category Form State
  const [catInput, setCatInput] = useState("");
  const [editingCat, setEditingCat] = useState(null);

  // Editor Config
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link', 'image'], 
      ['clean']
    ],
  };

  const getHeaders = useCallback(() => {
    return { "Authorization": `Bearer ${token}` };
  }, [token]);

  // --- Fetching Logic ---
  const fetchPosts = useCallback(async () => {
    try {
        const res = await fetch(`${API}/admin/blog/posts`, { headers: getHeaders() });
        if(res.ok) setPosts(await res.json());
    } catch(e) { console.error(e); }
  }, [getHeaders]);

  const fetchCategories = useCallback(async () => {
    try {
        const res = await fetch(`${API}/public/blog/categories`);
        if(res.ok) setCategories(await res.json());
    } catch(e) { console.error(e); }
  }, []);

  const fetchComments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/blog/comments`, { headers: getHeaders() });
      if (res.ok) setComments(await res.json());
    } catch (e) { console.error("Error loading comments", e); }
  }, [getHeaders]);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchComments(); 
  }, [fetchPosts, fetchCategories, fetchComments]);


  // --- Post Handlers ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(form).forEach(key => formData.append(key, form[key]));
    if (imageFile) formData.append("image", imageFile);

    const url = editingPost 
      ? `${API}/admin/blog/post/${editingPost.id}`
      : `${API}/admin/blog/post`;
    
    const method = editingPost ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: { "Authorization": `Bearer ${token}` }, 
        body: formData
      });

      if (res.ok) {
        setForm({ title: "", excerpt: "", content: "", categoryId: "", scheduledAt: "", keywords: "", allowComments: true });
        setImageFile(null);
        setEditingPost(null);
        fetchPosts();
        alert("Post saved successfully!");
        setTab("posts");
      } else {
        alert("Failed to save post");
      }
    } catch (err) { console.error(err); alert("Error saving post"); }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      excerpt: post.excerpt || "",
      content: post.content,
      // FIX: Use categoryId (standard sequelize FK) instead of BlogCategoryId
      categoryId: post.categoryId || "", 
      scheduledAt: post.scheduledAt ? new Date(post.scheduledAt).toISOString().split('T')[0] : "",
      keywords: post.keywords || "",
      allowComments: post.allowComments
    });
    setTab("create");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleDelete = async (id) => {
    if(!window.confirm("Delete this post?")) return;
    await fetch(`${API}/admin/blog/post/${id}`, { method: "DELETE", headers: getHeaders() });
    fetchPosts();
  };

  // --- Category Handlers ---
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!catInput.trim()) return;

    const url = editingCat 
      ? `${API}/admin/blog/categories/${editingCat.id}` 
      : `${API}/admin/blog/categories`;
    const method = editingCat ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
         method, 
         headers: { ...getHeaders(), "Content-Type": "application/json" },
         body: JSON.stringify({ name: catInput })
      });
      if (res.ok) {
         setCatInput("");
         setEditingCat(null);
         fetchCategories();
      }
    } catch (err) { console.error(err); }
  };

  const handleDeleteCategory = async (id) => {
     if(!window.confirm("Delete this category? Posts in this category may become uncategorized.")) return;
     try {
        await fetch(`${API}/admin/blog/categories/${id}`, { method: "DELETE", headers: getHeaders() });
        fetchCategories();
     } catch (err) { console.error(err); }
  };

  // --- Comment Handlers ---
  const handleDeleteComment = async (id) => {
    if(!window.confirm("Delete this comment permanently?")) return;
    try {
        const res = await fetch(`${API}/admin/blog/comment/${id}`, {
            method: "DELETE", headers: getHeaders()
        });
        if(res.ok) fetchComments();
    } catch(e) { console.error(e); }
  };

  const handleReplyComment = async (postId) => {
      const content = prompt("Type your reply as Admin:");
      if(!content) return;
      try {
          const res = await fetch(`${API}/admin/blog/reply`, {
              method: "POST",
              headers: { ...getHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({ postId, content })
          });
          if(res.ok) { alert("Reply posted!"); fetchComments(); }
      } catch(e) { console.error(e); }
  };


  return (
    <div className="blog-manager">
      <h2>Blog Management Dashboard</h2>
      
      <div className="tabs">
        <button className={tab === "posts" ? "active" : ""} onClick={() => setTab("posts")}>All Posts</button>
        <button className={tab === "create" ? "active" : ""} onClick={() => { setTab("create"); setEditingPost(null); }}>
            <FaPlus style={{ marginRight: 5 }} /> {editingPost ? "Edit Post" : "New Post"}
        </button>
        <button className={tab === "categories" ? "active" : ""} onClick={() => setTab("categories")}>Categories</button>
        <button className={tab === "comments" ? "active" : ""} onClick={() => setTab("comments")}>Comments</button>
      </div>

      {/* --- CREATE / EDIT POST TAB --- */}
      {(tab === "create") && (
        <form onSubmit={handleSubmit} className="post-form">
          <h3 style={{marginTop:0}}>{editingPost ? "Edit Post" : "Create New Post"}</h3>
          
          <div className="form-row">
            <div className="form-group">
                <label>Post Title</label>
                <input 
                  placeholder="Enter title..." 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})} 
                  required 
                />
            </div>
            <div className="form-group">
                <label>Category</label>
                <select 
                  value={form.categoryId} 
                  onChange={e => setForm({...form, categoryId: e.target.value})} 
                >
                  <option value="">Select Category...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
          </div>

          <div className="form-row">
              <div className="form-group">
                  <label>Publish Date</label>
                  <input type="date" value={form.scheduledAt} onChange={e => setForm({...form, scheduledAt: e.target.value})} />
              </div>
              <div className="form-group">
                  <label>Keywords</label>
                  <input placeholder="e.g. tech, news" value={form.keywords} onChange={e => setForm({...form, keywords: e.target.value})} />
              </div>
          </div>

          <div className="form-group">
              <label>Excerpt</label>
              <textarea rows="2" placeholder="Short summary..." value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} />
          </div>

          <div className="form-group editor-wrapper">
            <label>Content</label>
            <ReactQuill theme="snow" value={form.content} onChange={val => setForm({...form, content: val})} modules={modules} />
          </div>

          <div className="form-footer">
              <div className="file-input-wrapper">
                  <label style={{marginBottom: 5, display:'block'}}>Featured Image</label>
                  <input type="file" onChange={e => setImageFile(e.target.files[0])} accept="image/*" />
              </div>
              <label className="checkbox-wrapper">
                  <input type="checkbox" checked={form.allowComments} onChange={e => setForm({...form, allowComments: e.target.checked})} />
                  <span>Allow Comments</span>
              </label>
              <div className="button-group">
                  <button type="button" className="cancel-btn" onClick={() => { setTab("posts"); setEditingPost(null); }}>Cancel</button>
                  <button type="submit" className="submit-btn">{editingPost ? "Update Post" : "Publish Post"}</button>
              </div>
          </div>
        </form>
      )}

      {/* --- ALL POSTS LIST --- */}
{/* --- ALL POSTS LIST --- */}
      {tab === "posts" && (
        <ul className="vertical-list">
          {posts.length === 0 ? <p style={{color:'#777'}}>No posts found.</p> : posts.map(p => (
            <li key={p.id}>
              <div className="post-info">
                <h4>{p.title}</h4>
                <div className="post-meta" style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>{new Date(p.scheduledAt).toLocaleDateString()}</span>
                    <span>&bull;</span>
                    <span>{p.BlogCategory?.name || "Uncategorized"}</span>
                    
                    <span>&bull;</span>
                    <span style={{ color: '#007bff' }}>{p.views || 0} Views</span>
                    
                    <span>&bull;</span>
                    <span style={{ color: '#e44d26' }}>{p.likes || 0} Likes</span>
                    
                    {/* ✅ UPDATED: Safe check for comments */}
                    <span>&bull;</span>
                    <span style={{ color: '#28a745' }}>
                        {(p.BlogComments || []).length} Comments
                    </span>
                </div>
              </div>
              <div className="actions">
                <button className="edit-btn" onClick={() => handleEdit(p)} title="Edit"><FaEdit /></button>
                <button className="delete-btn" onClick={() => handleDelete(p.id)} title="Delete"><FaTrash /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* --- CATEGORIES TAB --- */}
       {tab === "categories" && (
          <div>
            <form onSubmit={handleCategorySubmit} className="add-input" style={{ alignItems: 'center' }}>
                <input 
                  value={catInput} 
                  onChange={(e) => setCatInput(e.target.value)}
                  placeholder="Category Name" 
                  required
                />
                <button type="submit" style={{width: 'auto', whiteSpace: 'nowrap'}}>
                   {editingCat ? "Update Category" : "Add Category"}
                </button>
                {editingCat && (
                  <button 
                    type="button" 
                    className="cancel-btn"
                    style={{marginLeft: 10}} 
                    onClick={() => { setEditingCat(null); setCatInput(""); }}
                  >
                    Cancel
                  </button>
                )}
            </form>
            
            <ul className="vertical-list" style={{ marginTop: 20 }}>
               {categories.length === 0 && <p style={{color: '#777'}}>No categories found.</p>}
               {categories.map(c => (
                   <li key={c.id}>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      <div className="actions">
                          <button className="edit-btn" onClick={() => { setEditingCat(c); setCatInput(c.name); }} title="Edit">
                             <FaEdit />
                          </button>
                          <button className="delete-btn" onClick={() => handleDeleteCategory(c.id)} title="Delete">
                             <FaTrash />
                          </button>
                      </div>
                   </li>
               ))}
            </ul>
          </div>
      )}

      {/* --- COMMENTS TAB --- */}
      {tab === "comments" && (
          <ul className="vertical-list">
              {comments.length === 0 && <p>No comments to moderate.</p>}
              {comments.map(c => (
                  <li key={c.id}>
                      <div style={{flex: 1}}>
                          <strong>{c.authorName}</strong> on <em>{c.BlogPost?.title}</em>
                          {c.isAdminReply && <span style={{marginLeft:10, fontSize:'0.8rem', background:'#eee', padding:'2px 6px', borderRadius:4}}>Admin Reply</span>}
                          <p style={{margin:"5px 0", color: "#555"}}>{c.content}</p>
                      </div>
                      <div className="actions">
                          {!c.isAdminReply && (
                            <button className="reply-btn" onClick={() => handleReplyComment(c.BlogPostId)} title="Reply" style={{background: "#2c3e50"}}>
                                <FaReply />
                            </button>
                          )}
                          <button className="delete-btn" onClick={() => handleDeleteComment(c.id)} title="Delete">
                            <FaTrash />
                          </button>
                      </div>
                  </li>
              ))}
          </ul>
      )}
    </div>
  );
}